/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import { useSelector } from 'react-redux';

import { TIME_TYPE_FLAG } from '../../../../constants/Flags';
import OnTraccrNumberInput from '../../../../common/inputs/OnTraccrNumberInput';
import TypeOfTimePicker from './TypeOfTimePicker';
import { isNullOrUndefined } from '../../../../helpers/helpers';
import { getTimeText } from '../../../../clock/ManualEntry/manualEntryHelpers';

function TimeEntryTableHoursInput(props) {
  const {
    date,
    timezone,
    value,
    onChange,
    onBlur,
    onConfirm,
    hourBased,
  } = props;

  const { paidFlags = [] } = useSelector((state) => state.settings.company);
  const { settings = {} } = useSelector((state) => state.settings.company);

  const {
    enableManualOT = false,
    showOriginalTimes = false,
  } = settings;

  const [type, setType] = useState('regular');

  const hasTimeTypeFlag = useMemo(() => paidFlags.includes(TIME_TYPE_FLAG), [paidFlags]);
  const safeDate = useMemo(() => (date.isValid ? date : DateTime.local()), [date]);

  const [startTimeKey, endTimeKey] = useMemo(() => {
    if (type === 'regular') {
      return [
        'startTime',
        'endTime',
      ];
    }

    const start = `${type}StartTime`;
    const end = `${type}EndTime`;

    return [
      start,
      end,
    ];
  }, [type]);

  const luxonEnd = useMemo(() => {
    const endMillis = value[endTimeKey];
    const end = endMillis ? DateTime.fromMillis(endMillis, { zone: timezone }) : null;

    return end ? safeDate.set({
      hour: end.hour,
      minute: end.minute,
    }) : safeDate.set({
      hour: 0,
      minute: 0,
    });
  }, [safeDate, endTimeKey, value, timezone]);

  const readOnlyTimeText = getTimeText({
    hourBased: true,
    type: null,
    startTime: value[startTimeKey] ?? null,
    endTime: value[endTimeKey] ?? null,
    originalStart: value.originalStart,
    originalEnd: value.originalEnd,
    timezone,
  }, { showZeroTimes: true, showOriginalTimes });

  const handleDecimalChange = (decimalValue) => {
    if (isNullOrUndefined(decimalValue) || Number.isNaN(parseFloat(decimalValue))) {
      onChange({ ...value, [startTimeKey]: null, [endTimeKey]: null });
      return;
    }

    const totalMinutes = decimalValue * 60;

    const dt = safeDate.startOf('day').set({
      hour: Math.floor(totalMinutes / 60),
      minute: Math.round(totalMinutes % 60),
    });

    onChange({
      ...value,
      [startTimeKey]: dt.set({ hour: 0, minute: 0 }).toMillis(),
      [endTimeKey]: dt.toMillis(),
    });
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
      }}
    >
      {hasTimeTypeFlag && (
        <TypeOfTimePicker type={type} setType={setType} enableManualOT={enableManualOT} />
      )}
      {hourBased ? (
        <OnTraccrNumberInput
          min={0}
          max={24}
          step={0.1}
          precision={2}
          value={
            (luxonEnd.hour || luxonEnd.minute)
              ? luxonEnd.hour + luxonEnd.minute / 60
              : undefined
          }
          onChange={handleDecimalChange}
          parser={(newValue) => newValue.replace(/[^\d.]/g, '')}
          onPressEnter={onConfirm}
          onBlur={(event) => {
            onBlur?.(event);
          }}
        />
      ) : (
        <span>
          {readOnlyTimeText ?? '0h'}
        </span>
      )}
    </div>
  );
}

TimeEntryTableHoursInput.propTypes = {
  date: PropTypes.instanceOf(DateTime).isRequired,
  timezone: PropTypes.string,
  value: PropTypes.shape({
    startTime: PropTypes.number,
    endTime: PropTypes.number,
    originalStart: PropTypes.number,
    originalEnd: PropTypes.number,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  style: PropTypes.shape({}),
  onConfirm: PropTypes.func.isRequired,
  hourBased: PropTypes.bool,
};

TimeEntryTableHoursInput.defaultProps = {
  timezone: DateTime.local().zoneName,
  style: {},
  hourBased: true,
};

export default TimeEntryTableHoursInput;
